import { useEffect, useMemo, useState } from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@/components/ui/command';
import { useCommandPaletteStore } from '@/lib/stores/command-palette';
import { useRoleCan } from '@/features/access/use-role-access';
import { commandPaletteFilter } from './command-palette-filter';
import { PageResults } from './PageResults';
import { QuickActions } from './QuickActions';
import { quickActionsCount } from './quick-actions';
import { CommandResultsBar, type CommandScope } from './CommandResultsBar';
import { useRecentPages } from './use-recent-pages';
import { isMacPlatform } from './command-palette-platform';
import { COMMAND_PAGES, visibleCommandPages, type CommandPageItem } from './command-palette-registry';

/**
 * The app-wide ⌘K / Ctrl+K command palette (TRU command-palette spec).
 * Mounted once in `AppShell`, alongside the sidebar's search trigger button
 * — both open it through `useCommandPaletteStore`, the shared source of
 * truth for open state (genuinely global/cross-component, unlike a
 * page-local dialog, so it's the one modal in this app NOT driven by the
 * URL: it isn't page content, so there is no route to encode it in).
 *
 * The keydown listener is global (works regardless of focus, including
 * inside a text input) and mounted unconditionally — cheap (one listener,
 * no work until the key combo matches) — while everything inside the dialog
 * (the page registry's role-gated filtering, the API-key gate's
 * capabilities fetch) only mounts once `isOpen` is true, via Radix
 * Dialog's own unmount-when-closed behavior.
 */
export function CommandPalette() {
  const isOpen = useCommandPaletteStore((s) => s.isOpen);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<CommandScope>('all');
  const { recentIds, recordVisit } = useRecentPages();

  // Computed once here (not inside `PageResults`) so the result-count bar
  // and the rendered list read the exact same role/plan-filtered array.
  const { can } = useRoleCan();
  const visiblePages = useMemo(() => visibleCommandPages(COMMAND_PAGES, can), [can]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const modifierPressed = isMacPlatform() ? e.metaKey : e.ctrlKey;
      if (modifierPressed && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!useCommandPaletteStore.getState().isOpen);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setQuery('');
      setScope('all');
    }
  }

  function handleSelectPage(page: CommandPageItem) {
    recordVisit(page.id);
    handleOpenChange(false);
  }

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      title="Command palette"
      description="Search pages and run quick actions"
      filter={commandPaletteFilter}
    >
      <CommandInput
        placeholder="Search pages and actions…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandResultsBar
        scope={scope}
        onScopeChange={setScope}
        query={query}
        pagesCount={visiblePages.length}
        actionsCount={quickActionsCount(can)}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {/* Order is deliberate: Recent → Quick actions → every other page.
            Quick actions outrank the full page list (the thing a returning
            user is most likely to want after their own history), but never
            outrank Recent. `PageResults` renders twice to straddle them —
            see its `PageResultsSection`. With a query typed there is no
            Recent, so this reads Quick actions → Pages. */}
        {scope !== 'actions' && (
          <PageResults
            section="recent"
            query={query}
            recentIds={recentIds}
            visiblePages={visiblePages}
            onSelectPage={handleSelectPage}
          />
        )}
        {scope !== 'pages' && <QuickActions onRun={() => handleOpenChange(false)} />}
        {scope !== 'actions' && (
          <PageResults
            section="rest"
            query={query}
            recentIds={recentIds}
            visiblePages={visiblePages}
            onSelectPage={handleSelectPage}
          />
        )}
      </CommandList>
      <div className="flex items-center gap-4 border-t border-border px-3 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-sans">↑↓</kbd>
          Navigate
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-sans">↵</kbd>
          Select
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-sans">Esc</kbd>
          Close
        </span>
      </div>
    </CommandDialog>
  );
}
